import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { CloseJobDialog } from './CloseJobDialog';
import { updateJobStatus } from '../api/jobs-api';
import type { JobStatus } from '../types';

interface JobStatusControlProps {
  jobId: number;
  currentStatus: JobStatus;
  onStatusChanged: (newStatus: JobStatus) => void;
}

export function JobStatusControl({ jobId, currentStatus, onStatusChanged }: JobStatusControlProps) {
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCloseJob = () => {
    setIsCloseDialogOpen(true);
  };

  const handleSendToCollections = async () => {
    if (!window.confirm('Are you sure you want to send this job to Collections?')) {
      return;
    }

    setIsSubmitting(true);
    try {
      await updateJobStatus(jobId, {
        newStatus: 'Collections',
      });
      onStatusChanged('Collections');
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to update job status');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseConfirm = async (
    closingComments: string,
    completedDate?: string,
    closedDate?: string
  ) => {
    setIsSubmitting(true);
    try {
      await updateJobStatus(jobId, {
        newStatus: 'Closed',
        closingComments,
        completedDate,
        closedDate,
      });
      setIsCloseDialogOpen(false);
      onStatusChanged('Closed');
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to close job');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine which buttons to show based on current status
  const showCloseButton = currentStatus === 'Active';
  const showCollectionsButton = currentStatus === 'Active';

  if (!showCloseButton && !showCollectionsButton) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-1.5">
        {showCloseButton && (
          <Button
            onClick={handleCloseJob}
            disabled={isSubmitting}
            variant="ghost"
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-black hover:bg-gray-50"
          >
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 8L24 24M8 24L24 8" stroke="#d71f20" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Close Job
          </Button>
        )}

        {showCollectionsButton && (
          <Button
            onClick={handleSendToCollections}
            disabled={isSubmitting}
            variant="ghost"
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-black hover:bg-gray-50"
          >
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 12C4 9.79086 5.79086 8 8 8H24C26.2091 8 28 9.79086 28 12V24C28 26.2091 26.2091 28 24 28H8C5.79086 28 4 26.2091 4 24V12Z" stroke="#d71f20" strokeWidth="2"/>
              <path d="M16 4V8M12 16H20M12 20H20" stroke="#d71f20" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Send to Collections
          </Button>
        )}
      </div>

      <CloseJobDialog
        open={isCloseDialogOpen}
        onOpenChange={setIsCloseDialogOpen}
        onConfirm={handleCloseConfirm}
        isLoading={isSubmitting}
      />
    </>
  );
}
